import { FC, useState } from 'react';

import { GiftCheckoutDeliveryDate } from '../hooks/useGiftCheckoutDeliveryDate';
import {
  deliveryDateBlockedReason,
  formatDeliveryDate,
  formatPaymentDeadline,
  isPaymentDeadlineToday,
  resolvePaymentDeadline,
  weekdayOfDate,
} from '../utils/giftDeliveryDate.util';

interface IProps {
  /** useGiftCheckoutDeliveryDate 반환값 그대로 */
  deliveryDate: GiftCheckoutDeliveryDate;
}

/** 도착 시간대 강조 띠 — 주문서·상품 상세 공용 */
export const GiftDeliveryArrivalNotice: FC<{ notice: string; className?: string }> = ({
  notice,
  className = '',
}) => (
  <div className={`rounded-lg px-3 py-2 text-white theme-bg-main ${className}`}>
    <p className="break-keep text-sm font-bold leading-snug">{notice}</p>
  </div>
);

const WEEKDAY_HEADERS = ['일', '월', '화', '수', '목', '금', '토'];

/** 'YYYY-MM' 한 달의 일 수 */
const daysInMonth = (month: string): number => {
  const [year = 0, mon = 1] = month.split('-').map(Number);
  return new Date(Date.UTC(year, mon, 0)).getUTCDate();
};

/**
 * 주문서 "받으실 날짜" — 날짜 지정 브랜드(datePolicy) 일괄배송 주문에서만 그린다.
 * 월 달력에서 받을 수 있는 날만 누를 수 있다(마감시각 전이면 내일부터, 지났으면 모레부터 — 정책값).
 * 글은 최소한으로 — 달력과 그 아래 도착 안내 띠 하나만(고르면 날짜가 붙는다). 글씨 크기는 주문서의 다른 섹션(본문 text-sm)과 맞춘다.
 */
export const GiftDeliveryDateSelector: FC<IProps> = ({ deliveryDate }) => {
  const { policy, choices, selectedDate, selectDate, expired, sectionRef } = deliveryDate;
  // 보고 있는 달 'YYYY-MM' — 사용자가 ◀▶로 넘긴 값. 선택 가능한 달 범위를 벗어나면 첫 달로 본다
  const [pickedMonth, setPickedMonth] = useState('');
  if (!policy || !choices) return null;

  // 고를 수 있는 날(휴무 제외)과, 달력에 보여 줄 달 범위(선택지의 첫 달 ~ 마지막 달)
  const selectable = new Set(choices.options.filter((o) => !o.closed).map((o) => o.date));
  const months = Array.from(new Set(choices.options.map((o) => o.date.slice(0, 7))));
  const firstSelectableMonth = (
    choices.earliestSelectable ?? choices.earliestByLead
  ).slice(0, 7);
  const visibleMonth = months.includes(pickedMonth) ? pickedMonth : firstSelectableMonth;
  const monthIndex = months.indexOf(visibleMonth);
  const prevMonth = months[monthIndex - 1];
  const nextMonth = months[monthIndex + 1];

  const [year, month] = visibleMonth.split('-').map(Number);
  const leadingBlanks = weekdayOfDate(`${visibleMonth}-01`);
  const days = Array.from({ length: daysInMonth(visibleMonth) }, (_, i) => {
    const date = `${visibleMonth}-${String(i + 1).padStart(2, '0')}`;
    return { date, day: i + 1, weekday: (leadingBlanks + i) % 7 };
  });

  const paymentDeadline = selectedDate
    ? resolvePaymentDeadline(policy, selectedDate)
    : null;

  return (
    <div ref={sectionRef} className="bg-white p-4">
      <h3 className="mb-3 font-bold">
        받으실 날짜 <span className="text-red-500">*</span>
      </h3>

      {!choices.earliestSelectable && (
        <p className="mt-2 break-keep text-sm font-bold text-red-500">
          지금은 선택할 수 있는 날짜가 없습니다. 고객센터로 문의해주세요.
        </p>
      )}

      {/* 달력 — 받을 수 있는 날만 누를 수 있고, 그 외(지난 날·휴무·범위 밖)는 흐리게 */}
      <div className="mt-3 rounded-lg border border-gray-200 p-3">
        <div className="flex items-center justify-between">
          <button
            type="button"
            aria-label="이전 달"
            disabled={!prevMonth}
            onClick={() => prevMonth && setPickedMonth(prevMonth)}
            className="px-3 py-1 text-base font-bold text-gray-700 disabled:text-gray-200"
          >
            ‹
          </button>
          <p className="text-sm font-bold">
            {year}년 {month}월
          </p>
          <button
            type="button"
            aria-label="다음 달"
            disabled={!nextMonth}
            onClick={() => nextMonth && setPickedMonth(nextMonth)}
            className="px-3 py-1 text-base font-bold text-gray-700 disabled:text-gray-200"
          >
            ›
          </button>
        </div>

        <div className="mt-2 grid grid-cols-7 text-center text-xs text-gray-500">
          {WEEKDAY_HEADERS.map((label, weekday) => (
            <span
              key={label}
              className={`py-1 ${
                weekday === 0 ? 'text-red-500' : weekday === 6 ? 'text-blue-500' : ''
              }`}
            >
              {label}
            </span>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-y-1">
          {Array.from({ length: leadingBlanks }, (_, i) => (
            <span key={`blank-${i}`} />
          ))}
          {days.map(({ date, day, weekday }) => {
            const enabled = selectable.has(date);
            const selected = date === selectedDate;
            return (
              <button
                key={date}
                type="button"
                aria-disabled={!enabled}
                aria-pressed={selected}
                aria-label={formatDeliveryDate(date)}
                onClick={() => {
                  if (enabled) {
                    selectDate(date);
                    return;
                  }
                  // 못 고르는 날도 눌리게 두고 이유를 알림창으로 알려 준다 — 아무 반응이 없거나
                  // 작은 글씨만 뜨면 왜 안 되는지 알아채기 어렵다 (주문서의 다른 입력 안내와 같은 방식)
                  const reason = deliveryDateBlockedReason(policy, choices, date);
                  if (reason) alert(reason);
                }}
                className={`mx-auto flex aspect-square w-full max-w-[40px] items-center justify-center rounded-full text-sm ${
                  selected
                    ? 'font-bold text-white theme-bg-main'
                    : !enabled
                    ? 'text-gray-300'
                    : weekday === 0
                    ? 'font-bold text-red-500'
                    : weekday === 6
                    ? 'font-bold text-blue-500'
                    : 'font-bold text-gray-900'
                }`}
              >
                {day}
              </button>
            );
          })}
        </div>
      </div>

      {expired && !selectedDate && (
        <p className="mt-2 break-keep text-xs font-bold text-red-500">
          결제 마감이 지났습니다. 날짜를 다시 골라 주세요.
        </p>
      )}

      {/* 도착 안내는 달력 아래 한 곳에만 — 고르기 전엔 강조 문구, 고르면 앞에 날짜가 붙는다 (위아래 반복 금지) */}
      {(selectedDate || policy.arrivalNotice) && (
        <GiftDeliveryArrivalNotice
          className="mt-3"
          notice={
            selectedDate
              ? `${formatDeliveryDate(selectedDate)} ${
                  policy.arrivalNotice || '배송됩니다.'
                }`
              : policy.arrivalNotice
          }
        />
      )}
      {/* 결제 기한은 오늘이 기한일 때만 한 줄 — 며칠 뒤 날짜를 고른 사람에겐 필요 없는 말이다 */}
      {isPaymentDeadlineToday(paymentDeadline) && (
        <p className="mt-1.5 break-keep text-xs text-gray-600">
          <span className="font-bold text-red-500">
            {formatPaymentDeadline(paymentDeadline)} 전
          </span>
          까지 결제해 주세요. 늦으면 날짜가 늦춰집니다.
        </p>
      )}
    </div>
  );
};
